import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ThemeContext } from "./themeContext";
import type { AppTheme } from "./themeTypes";
import { DEFAULT_APP_THEME, isAppTheme, THEME_STORAGE_KEY } from "./themeTypes";

function themeOf(storedTheme: string | null): AppTheme {
  const normalizedTheme = storedTheme === "nebula" ? "neon" : storedTheme;
  return isAppTheme(normalizedTheme) ? normalizedTheme : DEFAULT_APP_THEME;
}

export default function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<AppTheme>(() => themeOf(window.localStorage.getItem(THEME_STORAGE_KEY)));

  // A theme chosen in another tab of the app, such as the help beside it, shows here too (WA-6).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) setTheme(themeOf(event.newValue));
    };
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("storage", onStorage); };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = theme === "dark" || theme === "neon" ? "dark" : "light";
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
